import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { fetchArticles, fetchCategories } from "@/lib/blogApi";
import { BlogNav, BlogFooter, Marquee, MaskedLines, Reveal, ArticleCard, formatDate } from "@/components/blog/shared";
import { openCalendly, BOOKING_URL } from "@/lib/calendly";
import "@/blog.css";

const FeaturedSpotlight = ({ article }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  if (!article) return null;
  return (
    <section className="container-main mb-24" data-testid="featured-article">
      <Reveal>
        <div className="chapter-label mb-8">01 — Featured</div>
      </Reveal>
      <Reveal delay={0.1}>
        <Link
          to={`/blog/${article.slug}`}
          className="group grid lg:grid-cols-2 border border-[#E7E5E4] bg-white hover:border-[rgba(124,58,237,0.3)] hover:shadow-[0_25px_50px_-30px_rgba(10,10,10,0.15)] transition-all duration-500 overflow-hidden rounded-2xl"
        >
          <div ref={ref} className="relative overflow-hidden aspect-[16/10] lg:aspect-auto lg:min-h-[420px] bg-[#F5F4F0]">
            <motion.img
              style={{ y, scale: 1.12 }}
              src={article.featured_image_url}
              alt={article.featured_image_alt || article.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <div className="p-8 md:p-14 flex flex-col justify-center">
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-widest font-heading font-semibold mb-6">
              <span className="text-[#7C3AED]">{article.category_name}</span>
              <span className="w-1 h-1 bg-[#E7E5E4] rounded-full" />
              <span className="text-[#737373]">{formatDate(article.published_at)}</span>
              <span className="text-[#737373]">· {article.reading_time} min</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#0A0A0A] leading-tight mb-5 group-hover:text-[#7C3AED] transition-colors">
              {article.title}
            </h2>
            <p className="text-[#525252] leading-relaxed mb-8">{article.excerpt}</p>
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#7C3AED] font-heading uppercase tracking-widest">
              Read the breakdown <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </span>
          </div>
        </Link>
      </Reveal>
    </section>
  );
};

export default function BlogList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "";
  const page = parseInt(searchParams.get("page") || "1", 10);

  const [data, setData] = useState(null);
  const [featured, setFeatured] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories().then(setCategories).catch(() => {});
    fetchArticles({ featured: true, per_page: 1 })
      .then((d) => setFeatured(d.articles[0] || null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchArticles({ page, per_page: 9, category: category || undefined })
      .then(setData)
      .catch(() => setData({ articles: [], total: 0, total_pages: 1 }))
      .finally(() => setLoading(false));
  }, [category, page]);

  const setFilter = (slug) => {
    const params = {};
    if (slug) params.category = slug;
    setSearchParams(params);
  };
  const setPage = (p) => {
    const params = {};
    if (category) params.category = category;
    if (p > 1) params.page = String(p);
    setSearchParams(params);
    window.scrollTo({ top: 0 });
  };

  const canonical = `${window.location.origin}/blog`;

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="blog-list-page">
      <Helmet>
        <title>Sweepstakes Marketing Blog — Paid Media, Creatives &amp; LTV | Voveron</title>
        <meta name="description" content="Field notes on sweepstakes paid media, creative testing, tracking, attribution and player LTV — written for US sweepstakes operators by Voveron." />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="Sweepstakes Marketing Blog — Paid Media, Creatives & LTV | Voveron" />
        <meta property="og:description" content="Field notes on sweepstakes paid media, creative testing, tracking and player LTV — written for US sweepstakes operators." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        {featured?.featured_image_url && <meta property="og:image" content={featured.featured_image_url} />}
      </Helmet>
      <BlogNav />

      {/* Kinetic hero (light) */}
      <header className="pt-28 md:pt-36 pb-12 md:pb-16 relative overflow-hidden">
        <div className="grid-overlay absolute inset-0 opacity-60 pointer-events-none" />
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#7C3AED]/[0.05] blur-3xl pointer-events-none" />
        <div className="container-main relative">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="font-heading text-[11px] md:text-xs font-semibold tracking-[0.28em] uppercase text-[#7C3AED] mb-6 md:mb-8"
            data-testid="blog-hero-eyebrow"
          >
            Voveron Journal — Field Notes
          </motion.p>
          <h1 className="font-heading font-bold text-[#0A0A0A] text-[2rem] sm:text-5xl lg:text-[4rem] leading-[1.08] lg:leading-[1.05] tracking-tight" data-testid="blog-hero-title">
            <MaskedLines lines={["The Operator's", <span key="e" className="text-[#7C3AED]">Edge.</span>]} delay={0.15} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55 }}
            className="text-[15px] md:text-lg text-[#525252] max-w-xl mt-6 md:mt-8 leading-relaxed"
          >
            No recycled growth-hacks. Tactical breakdowns of acquisition, creative and retention systems — written for sweepstakes operators who scale.
          </motion.p>
        </div>
      </header>

      <div className="mb-16 md:mb-24">
        <Marquee />
      </div>

      {!category && page === 1 && <FeaturedSpotlight article={featured} />}

      {/* Filter + grid */}
      <section className="container-main">
        <Reveal>
          <div className="chapter-label mb-8">02 — Browse</div>
        </Reveal>
        <Reveal delay={0.05}>
          <div className="flex flex-wrap gap-3 mb-12" data-testid="category-filters">
            <button className={`cat-pill ${!category ? "active" : ""}`} onClick={() => setFilter("")} data-testid="category-filter-all">
              All
            </button>
            {categories.map((c) => (
              <button
                key={c.slug}
                className={`cat-pill ${category === c.slug ? "active" : ""}`}
                onClick={() => setFilter(c.slug)}
                data-testid={`category-filter-${c.slug}`}
              >
                {c.name} <span className="opacity-60 ml-1">{c.article_count}</span>
              </button>
            ))}
          </div>
        </Reveal>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="articles-loading">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="border border-[#E7E5E4] bg-white rounded-2xl animate-pulse h-96" />
            ))}
          </div>
        ) : data?.articles.length ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="articles-grid">
            {data.articles.map((a, i) => (
              <ArticleCard key={a.id} article={a} index={i} />
            ))}
          </div>
        ) : (
          <p className="text-[#737373] py-20 text-center" data-testid="articles-empty">
            No articles in this category yet.
          </p>
        )}

        {/* Pagination */}
        {data && data.total_pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-16" data-testid="pagination">
            <button
              className="cat-pill disabled:opacity-30 disabled:cursor-not-allowed"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              data-testid="pagination-prev"
            >
              <ChevronLeft size={14} />
            </button>
            {[...Array(data.total_pages)].map((_, i) => (
              <button
                key={i}
                className={`cat-pill ${page === i + 1 ? "active" : ""}`}
                onClick={() => setPage(i + 1)}
                data-testid={`pagination-page-${i + 1}`}
              >
                {i + 1}
              </button>
            ))}
            <button
              className="cat-pill disabled:opacity-30 disabled:cursor-not-allowed"
              disabled={page >= data.total_pages}
              onClick={() => setPage(page + 1)}
              data-testid="pagination-next"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        )}
      </section>

      {/* CTA (light) */}
      <section className="container-main mt-28">
        <Reveal>
          <div className="border border-[#E7E5E4] bg-white p-10 md:p-16 text-center relative overflow-hidden rounded-2xl">
            <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full bg-[#7C3AED]/[0.05] blur-3xl pointer-events-none" />
            <h2 className="font-heading text-2xl md:text-[2rem] font-bold text-[#0A0A0A] mb-4 relative leading-tight">
              Reading is free. Scaling is a call away.
            </h2>
            <p className="text-[#525252] mb-8 max-w-lg mx-auto relative leading-relaxed">
              Get the same systems from these breakdowns applied to your acquisition — starting with a free audit.
            </p>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" onClick={openCalendly} className="relative inline-block">
              <button className="btn-primary px-7 py-3.5 text-sm font-semibold rounded-full" data-testid="blog-cta-button">
                Book Your Audit
              </button>
            </a>
          </div>
        </Reveal>
      </section>

      <BlogFooter />
    </div>
  );
}
